import { 
    getFirestore, 
    doc, 
    getDoc, 
    updateDoc, 
    increment,
    setDoc,
    Timestamp 
} from 'firebase/firestore';
import { Analytics, createEmptyAnalytics } from '../models/Analytics';

export class AnalyticsService {
    private static getAnalyticsRef(campaignId: string) {
        return doc(getFirestore(), 'analytics', campaignId);
    }

    static async initializeAnalytics(campaignId: string): Promise<void> {
        const analyticsRef = this.getAnalyticsRef(campaignId);
        const analyticsDoc = await getDoc(analyticsRef);
        
        if (!analyticsDoc.exists()) {
            await setDoc(analyticsRef, createEmptyAnalytics(campaignId));
        }
    }

    static async getAnalytics(campaignId: string): Promise<Analytics> {
        try {
            await this.initializeAnalytics(campaignId);
            const analyticsRef = this.getAnalyticsRef(campaignId);
            const analyticsDoc = await getDoc(analyticsRef);
            return analyticsDoc.data() as Analytics;
        } catch (error) {
            console.error('Error fetching analytics:', error);
            return createEmptyAnalytics(campaignId);
        }
    }

    private static getDates() {
        const now = new Date();
        const day = now.toISOString().split('T')[0];
        
        // Get week start (Sunday)
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay());
        const weekKey = weekStart.toISOString().split('T')[0];
        
        // Get month start
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const monthKey = monthStart.toISOString().split('T')[0];
        
        return { day, weekKey, monthKey };
    }

    static async trackImpression(
        campaignId: string, 
        viewDuration: number, 
        variationId?: string
    ): Promise<void> {
        try {
            await this.initializeAnalytics(campaignId);
            const analyticsRef = this.getAnalyticsRef(campaignId);
            const { day, weekKey, monthKey } = this.getDates();

            const baseUpdate: any = {
                totalImpressions: increment(1),
                avgViewDuration: increment(viewDuration),
                [`dailyMetrics.${day}.impressions`]: increment(1),
                [`dailyMetrics.${day}.viewDuration`]: increment(viewDuration),
                [`weeklyMetrics.${weekKey}.impressions`]: increment(1),
                [`weeklyMetrics.${weekKey}.viewDuration`]: increment(viewDuration),
                [`monthlyMetrics.${monthKey}.impressions`]: increment(1),
                [`monthlyMetrics.${monthKey}.viewDuration`]: increment(viewDuration),
                lastUpdated: Timestamp.now()
            };

            if (variationId) {
                // Update variation-level metrics as well
                baseUpdate[`variations.${variationId}.totalImpressions`] = increment(1);
                baseUpdate[`variations.${variationId}.avgViewDuration`] = increment(viewDuration);
                baseUpdate[`variations.${variationId}.dailyMetrics.${day}.impressions`] = increment(1);
                baseUpdate[`variations.${variationId}.dailyMetrics.${day}.viewDuration`] = increment(viewDuration);
                baseUpdate[`variations.${variationId}.weeklyMetrics.${weekKey}.impressions`] = increment(1);
                baseUpdate[`variations.${variationId}.weeklyMetrics.${weekKey}.viewDuration`] = increment(viewDuration);
                baseUpdate[`variations.${variationId}.monthlyMetrics.${monthKey}.impressions`] = increment(1);
                baseUpdate[`variations.${variationId}.monthlyMetrics.${monthKey}.viewDuration`] = increment(viewDuration);
            }

            await updateDoc(analyticsRef, baseUpdate);
        } catch (error) {
            console.error('Error tracking impression:', error);
        }
    }

    static async trackClick(
        campaignId: string, 
        variationId?: string
    ): Promise<void> {
        try {
            await this.initializeAnalytics(campaignId);
            const analyticsRef = this.getAnalyticsRef(campaignId);
            const { day, weekKey, monthKey } = this.getDates();

            const baseUpdate: any = {
                totalClicks: increment(1),
                [`dailyMetrics.${day}.clicks`]: increment(1),
                [`weeklyMetrics.${weekKey}.clicks`]: increment(1),
                [`monthlyMetrics.${monthKey}.clicks`]: increment(1),
                lastUpdated: Timestamp.now()
            };

            if (variationId) {
                baseUpdate[`variations.${variationId}.totalClicks`] = increment(1);
                baseUpdate[`variations.${variationId}.dailyMetrics.${day}.clicks`] = increment(1);
                baseUpdate[`variations.${variationId}.weeklyMetrics.${weekKey}.clicks`] = increment(1);
                baseUpdate[`variations.${variationId}.monthlyMetrics.${monthKey}.clicks`] = increment(1);
            }

            await updateDoc(analyticsRef, baseUpdate);
        } catch (error) {
            console.error('Error tracking click:', error);
        }
    }

    static async deleteVariationAnalytics(
        campaignId: string, 
        variationId: string
    ): Promise<void> {
        try {
            const analyticsRef = this.getAnalyticsRef(campaignId);
            const analyticsDoc = await getDoc(analyticsRef);
            
            if (analyticsDoc.exists()) {
                const analyticsData = analyticsDoc.data() as Analytics;
                
                // Remove the variation from the variations object
                if (analyticsData.variations && analyticsData.variations[variationId]) {
                    delete analyticsData.variations[variationId];
                    
                    // Update the document with the variation removed
                    await updateDoc(analyticsRef, {
                        variations: analyticsData.variations,
                        lastUpdated: Timestamp.now()
                    });
                    
                    console.log(`Analytics data for variation ${variationId} has been deleted`);
                }
            }
        } catch (error) {
            console.error('Error deleting variation analytics:', error);
            throw error;
        }
    }
}